<template>
  <div class="contents">
    <div class="container-fluid">
      <!-- Header -->
      <div class="row">
        <div class="col-lg-12">
          <div class="breadcrumb-main user-member justify-content-sm-between">
            <div class="d-flex flex-wrap justify-content-center breadcrumb-main__wrapper">
              <h4 class="text-capitalize fw-500 breadcrumb-title">Laporan Scalev</h4>
            </div>
            <div class="action-btn">
              <button @click="exportCSV" class="btn btn-outline-success" style="margin:5px" :disabled="!rows.length">
                <i class="fas fa-file-csv"></i> Export CSV
              </button>
              <button @click="load" class="btn btn-secondary text-white" style="margin:5px">
                <i class="fas fa-sync"></i> Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Info -->
      <div class="alert alert-info mb-3">
        <small>
          <i class="fas fa-info-circle"></i>
          <b>Kriteria:</b> Akun terdeteksi memakai <b>Scalev</b> = memiliki device yang namanya mengandung kata "scalev".
          <b>Paket bulanan</b> = PAKET BULANAN / PAKET BULANAN PREMIUM (durasi 30 hari). Dipakai untuk keperluan bersih-bersih.
        </small>
      </div>

      <!-- Summary cards -->
      <div class="row" v-if="summary">
        <div class="col-md-4 col-sm-6 mb-3">
          <div class="card p-3" style="border-left:4px solid #1976d2">
            <div class="text-muted small">🔗 TOTAL AKUN PAKAI SCALEV</div>
            <div class="h4 mb-0 font-weight-bold text-primary">{{ summary.scalev_accounts }}</div>
            <small class="text-muted">punya device bernama "scalev"</small>
          </div>
        </div>
        <div class="col-md-4 col-sm-6 mb-3">
          <div class="card p-3" style="border-left:4px solid #e65100">
            <div class="text-muted small">📅 PAKET BULANAN</div>
            <div class="h4 mb-0 font-weight-bold" style="color:#e65100">{{ summary.monthly_accounts }}</div>
            <small class="text-muted">akun Scalev yang paketnya bulanan (target bersih-bersih)</small>
          </div>
        </div>
        <div class="col-md-4 col-sm-6 mb-3">
          <div class="card p-3" style="border-left:4px solid #2e7d32">
            <div class="text-muted small">📋 DITAMPILKAN</div>
            <div class="h4 mb-0 font-weight-bold" style="color:#2e7d32">{{ rows.length }}</div>
            <small class="text-muted">baris sesuai filter saat ini</small>
          </div>
        </div>
      </div>

      <!-- Breakdown per paket -->
      <div class="row" v-if="summary && summary.breakdown && summary.breakdown.length">
        <div class="col-md-6 mb-3">
          <div class="card p-3">
            <h6 class="font-weight-bold"><i class="fas fa-tags text-primary"></i> Sebaran Paket (semua akun Scalev)</h6>
            <table class="table table-sm mt-2">
              <thead><tr><th>Paket</th><th class="text-center">Durasi</th><th class="text-right">Akun</th></tr></thead>
              <tbody>
                <tr v-for="b in summary.breakdown" :key="b.package_id">
                  <td>
                    <span :class="b.duration === 30 ? 'badge badge-warning text-white' : 'badge badge-light'">{{ b.package_name }}</span>
                  </td>
                  <td class="text-center"><small>{{ durationLabel(b.duration) }}</small></td>
                  <td class="text-right font-weight-bold">{{ b.count }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Filter -->
      <div class="row mb-2">
        <div class="col-12">
          <div class="card p-3">
            <div class="d-flex align-items-center" style="gap:8px; flex-wrap:wrap">
              <span class="font-weight-bold">Tampilkan:</span>
              <button @click="setFilter('monthly')" :class="['btn btn-sm', filter.mode==='monthly' ? 'btn-primary':'btn-outline-primary']">
                Paket Bulanan saja
              </button>
              <button @click="setFilter('all')" :class="['btn btn-sm', filter.mode==='all' ? 'btn-primary':'btn-outline-primary']">
                Semua akun Scalev
              </button>
              <span class="font-weight-bold" style="margin-left:16px">| Cari:</span>
              <input v-model="filter.q" @input="onSearch" type="text" class="form-control form-control-sm" style="width:240px" placeholder="nama atau email" />
            </div>
          </div>
        </div>
      </div>

      <!-- Table -->
      <div class="row">
        <div class="col-12">
          <div class="card global-shadow border bg-white radius-xl w-100 mb-30">
            <div v-if="isLoading" class="text-center p-4">
              <div class="spinner-border" role="status"></div>
              <p class="mt-2 text-muted">Loading...</p>
            </div>
            <div v-else-if="!rows.length" class="text-center text-muted p-4">
              <p>Tidak ada akun yang cocok.</p>
            </div>
            <div v-else class="table-responsive">
              <table class="table table-hover mb-0">
                <thead class="bg-light">
                  <tr>
                    <th>#</th>
                    <th>User</th>
                    <th>Paket</th>
                    <th>Expired</th>
                    <th>Device Scalev</th>
                    <th>Daftar</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(r, i) in rows" :key="r.user_id">
                    <td>{{ i + 1 }}</td>
                    <td>
                      <div class="font-weight-bold">{{ r.name || '-' }}</div>
                      <div><small style="font-family:monospace">{{ r.email || '-' }}</small></div>
                      <small class="text-muted">{{ r.phone || '-' }} (#{{ r.user_id }})</small>
                    </td>
                    <td>
                      <span :class="r.duration === 30 ? 'badge badge-warning text-white' : 'badge badge-info'">{{ r.package_name }}</span>
                      <div><small class="text-muted">{{ formatRupiah(r.price) }}</small></div>
                    </td>
                    <td>
                      <div>{{ r.expired_human }}</div>
                      <span v-if="r.is_expired" class="badge badge-danger">Expired</span>
                      <span v-else class="badge badge-success">{{ r.days_left }} hari lagi</span>
                    </td>
                    <td><small :title="r.scalev_devices">{{ truncate(r.scalev_devices, 60) }}</small>
                      <div><small class="text-muted">{{ r.device_count }} device</small></div>
                    </td>
                    <td><small>{{ formatTime(r.created_at) }}</small></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  data() {
    return {
      isLoading: false,
      summary: null,
      rows: [],
      filter: { mode: "monthly", q: "" },
      searchDebounce: null,
    };
  },
  mounted() {
    this.load();
  },
  methods: {
    setFilter(m) {
      this.filter.mode = m;
      this.load();
    },
    onSearch() {
      clearTimeout(this.searchDebounce);
      this.searchDebounce = setTimeout(() => this.load(), 500);
    },
    async load() {
      this.isLoading = true;
      try {
        const params = { filter: this.filter.mode };
        if (this.filter.q) params.q = this.filter.q;
        const r = await axios.get("admin/scalev-report", { params });
        const d = r.data.data || {};
        this.summary = {
          scalev_accounts: d.scalev_accounts,
          monthly_accounts: d.monthly_accounts,
          breakdown: d.breakdown || [],
        };
        this.rows = d.data || [];
      } catch (err) {
        this.$toast.error("Gagal load: " + (err.response?.data?.message || err.message));
      } finally {
        this.isLoading = false;
      }
    },
    durationLabel(d) {
      if (d === 30) return "Bulanan";
      if (d >= 28 && d <= 31) return "Bulanan";
      if (d >= 180 && d <= 186) return "6 Bulan";
      if (d >= 360 && d <= 366) return "Tahunan";
      if (d >= 3) return d >= 3000 ? "Lifetime" : d + " hari";
      return d + " hari";
    },
    formatRupiah(n) {
      const num = parseInt(n) || 0;
      return "Rp " + num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    },
    formatTime(millis) {
      if (!millis) return "-";
      return new Date(parseInt(millis)).toLocaleDateString("id-ID", {
        timeZone: "Asia/Jakarta", day: "2-digit", month: "short", year: "2-digit",
      });
    },
    truncate(s, n) {
      if (!s) return "-";
      return s.length > n ? s.substring(0, n) + "..." : s;
    },
    exportCSV() {
      const head = ["No", "User ID", "Nama", "Email", "Phone", "Paket", "Harga", "Durasi", "Expired", "Sisa Hari", "Status", "Device Scalev", "Jumlah Device"];
      const lines = [head.join(",")];
      this.rows.forEach((r, i) => {
        const cols = [
          i + 1, r.user_id,
          `"${(r.name || "").replace(/"/g, "'")}"`,
          r.email || "", r.phone || "",
          `"${(r.package_name || "").replace(/"/g, "'")}"`,
          r.price || 0, r.duration || 0,
          r.expired_human || "", r.days_left ?? "",
          r.is_expired ? "Expired" : "Aktif",
          `"${(r.scalev_devices || "").replace(/"/g, "'")}"`,
          r.device_count || 0,
        ];
        lines.push(cols.join(","));
      });
      const blob = new Blob(["﻿" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `scalev-${this.filter.mode}-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    },
  },
};
</script>
